import {
  Box,
  Button,
  FlexBox,
  FormField,
  Icon,
  SelectionList,
  SelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { HookFormText } from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMatch } from 'react-router-dom';
import { useMount, useUnmount } from 'react-use';

import {
  Automation,
  automationsApi,
  AutomationTrigger,
} from 'apis/automations';
import { appActions } from 'app/app.actions';
import {
  NotificationType,
  useNotifications,
} from 'assets/hooks/useNotifications';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from '../../commandLauncher.module.scss';
import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

type MacroFormValues = {
  csid: string;
};

type MacroStep = 'list' | 'confirm';

/** Represents the macro scope list of the command launcher. */
export const MacroScopeList = (): JSX.Element => {
  // Match both /conversation/:csid and /conversation/:csid/activity-log
  const conversationRouteMatch = useMatch('/conversation/:csid/*');
  const addNotification = useNotifications();
  const dispatch = useAppDispatch();

  const {
    actions: allActions,
    isActive,
    scopeQuery,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
    setQuery,
  } = useCommandLauncher();

  useMount(() => ampli.commandPaletteScopeEntered({ scope: 'macro' }));

  // Form state management
  const form = useForm<MacroFormValues>({
    defaultValues: { csid: '' },
    mode: 'onChange',
  });

  // Two-step flow state
  const [step, setStep] = useState<MacroStep>('list');
  const [selectedMacro, setSelectedMacro] = useState<Automation | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  // Prevents auto-submit from Enter key propagation when transitioning to confirm step
  const [isFormReady, setIsFormReady] = useState(false);

  // Fetch macros (automations with EXTERNAL_TRIGGER)
  const { data: macrosData, isLoading } =
    automationsApi.useReadAutomationsQuery({
      trigger: AutomationTrigger.EXTERNAL_TRIGGER,
      limit: 100,
    });

  const [runAutomation] = automationsApi.useRunAutomationMutation();

  // Get current conversation ID from URL if on a conversation page
  const currentCsid = conversationRouteMatch?.params.csid ?? '';

  // Pre-fill csid when macro is selected and focus input
  useEffect(() => {
    if (step === 'confirm' && currentCsid) {
      form.setValue('csid', currentCsid, { shouldValidate: true });
    }
  }, [step, currentCsid, form]);

  // Mark form as ready after initial render to prevent Enter key propagation from SelectionList
  useEffect(() => {
    if (step === 'confirm') {
      // Use requestAnimationFrame to wait until after the current event loop
      const frameId = requestAnimationFrame(() => {
        setIsFormReady(true);
      });
      return () => cancelAnimationFrame(frameId);
    }
    setIsFormReady(false);
    return undefined;
  }, [step]);

  // Filter actions for this scope
  const actions = useMemo(
    () =>
      allActions.filter((action) =>
        action.scope?.includes(CommandLauncherScope.Macro),
      ),
    [allActions],
  );

  // Create actions from fetched macros
  useEffect(() => {
    if (!macrosData?.data?.automations) return;

    const macroActions: readonly CommandLauncherAction[] =
      macrosData.data.automations
        .filter((automation) => automation.active)
        .map((automation) =>
          createAction({
            label: automation.name,
            section: 'Macros',
            icon: 'merge',
            scope: [CommandLauncherScope.Macro],
            keepOpen: true, // Keep modal open to show confirmation form
            performFn: () => {
              setSelectedMacro(automation);
              setStep('confirm');
              form.clearErrors();
            },
          }),
        );

    const unregister = registerActions(macroActions);

    return () => unregister();
  }, [createAction, macrosData?.data?.automations, registerActions, form]);

  // Filter macros based on search query
  const filteredActions = useMemo(() => {
    if (!scopeQuery) return actions;

    const fuse = new Fuse(actions, {
      keys: ['label'],
      threshold: 0.3,
    });

    return fuse.search(scopeQuery).map(({ item }) => item);
  }, [actions, scopeQuery]);

  // Update async status
  useEffect(() => {
    setAsyncStatus(isLoading ? 'pending' : 'fulfilled');
  }, [isLoading, setAsyncStatus]);

  useUnmount(() => {
    setAsyncStatus('fulfilled');
    setStep('list');
    setSelectedMacro(null);
    setIsFormReady(false);
    form.reset();
  });

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandLauncherAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    performAction(event.selectedItem.value);
  };

  const handleRunMacro = async (values: MacroFormValues) => {
    if (!selectedMacro) return;

    const csid = parseInt(values.csid, 10);

    setIsRunning(true);

    try {
      await runAutomation({
        automationId: selectedMacro.id,
        csid,
      }).unwrap();

      // 204 - Macro triggered successfully
      addNotification({
        title: 'Macro triggered',
        text: `"${selectedMacro.name}" is now running on conversation #${csid}`,
        icon: 'merge',
        className: 'success',
        time: 5000, // Auto-dismiss after 5 seconds
      });

      // Clear the input query
      setQuery('');

      dispatch(appActions.hideModal({ type: 'app:commandLauncher' }));
    } catch (err: unknown) {
      const error = err as { status?: number };

      if (error.status === 400) {
        // 400 - Not accepted (validation error)
        addNotification(
          {
            title: 'Macro not accepted',
            text: 'The macro could not be triggered. Please check the conversation ID.',
          },
          NotificationType.Error,
        );
        form.setError('csid', {
          message: 'Macro not accepted. Please check the conversation ID.',
        });
      } else {
        // 500 or other errors
        addNotification(
          {
            title: 'Macro failed',
            text: 'An error occurred while triggering the macro.',
          },
          NotificationType.Error,
        );
        form.setError('csid', {
          message: 'Failed to run macro. Please try again.',
        });
      }
    } finally {
      setIsRunning(false);
    }
  };

  const handleCancel = () => {
    setStep('list');
    setSelectedMacro(null);
    setIsFormReady(false);
    form.reset();
  };

  // Confirmation step
  if (step === 'confirm' && selectedMacro) {
    const csidValue = form.watch('csid');

    return (
      <Box padding="250" className={styles.macroConfirm}>
        <form
          onSubmit={(e) => {
            // Prevent submission until form is ready (guards against Enter key propagation)
            if (!isFormReady) {
              e.preventDefault();
              return;
            }
            form.handleSubmit(handleRunMacro)(e);
          }}
        >
          <FlexBox flexDirection="column" gap="300">
            <FlexBox alignItems="center" gap="200">
              <Icon name="merge" size={20} />
              <span className={styles.macroName}>{selectedMacro.name}</span>
            </FlexBox>

            {selectedMacro.description && (
              <Text.Paragraph color="secondary">
                {selectedMacro.description}
              </Text.Paragraph>
            )}

            <FormField>
              <HookFormText
                name="csid"
                label="Conversation ID"
                control={form.control}
                rules={{
                  required: 'Conversation ID is required',
                  pattern: {
                    value: /^\d+$/,
                    message: 'Please enter a valid conversation ID',
                  },
                }}
                componentProps={{
                  autoFocus: true,
                  placeholder: 'Enter conversation ID...',
                }}
              />
              {currentCsid && !csidValue && (
                <Text.Paragraph variant="3" color="tertiary">
                  Current conversation: {currentCsid}
                </Text.Paragraph>
              )}
            </FormField>

            <FlexBox justifyContent="flex-end" gap="200">
              <Button
                variant="secondary"
                onClick={handleCancel}
                disabled={isRunning}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!form.formState.isValid || isRunning}
                isLoading={isRunning}
              >
                Run macro
              </Button>
            </FlexBox>
          </FlexBox>
        </form>
      </Box>
    );
  }

  // List step
  const hasNoMacros = !isLoading && filteredActions.length === 0;
  const hasSearchQuery = scopeQuery && scopeQuery.trim().length > 0;

  return (
    <>
      {hasNoMacros && (
        <div className={styles.emptyState}>
          {hasSearchQuery ? (
            <span>No macros found for "{scopeQuery}"</span>
          ) : (
            <span>No macros available</span>
          )}
        </div>
      )}
      {!hasNoMacros && (
        <SelectionList<CommandLauncherAction>
          isActive={isActive}
          onChange={handleSelectionListOnChange}
        >
          {getCommandLauncherActionItems(filteredActions)}
        </SelectionList>
      )}
    </>
  );
};
